import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";

const money = (v) => (v === null || v === undefined || v === "" || isNaN(Number(v)) ? "n/a" : "$" + Number(v).toFixed(2));

export default function AdminPricing() {
  const [meds, setMeds]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery]     = useState("");
  const [rate, setRate]       = useState(null);
  const [edits, setEdits]     = useState({});   // {pricing_id: {cost_cad, fixed_price}}
  const [saving, setSaving]   = useState({});
  const [saved, setSaved]     = useState({});

  useEffect(() => {
    api.get("/admin/pricing")
      .then(r => {
        const data = r.data || [];
        setMeds(data);
        setRate(data[0]?.fx_rate ?? null);
        setLoading(false);
      })
      .catch(() => { toast.error("Failed to load medications"); setLoading(false); });
  }, []);

  const filtered = query.trim()
    ? meds.filter(m => m.name.toLowerCase().includes(query.toLowerCase()))
    : meds;

  const getVal = (m, field) => {
    const e = edits[m.pricing_id];
    if (e && e[field] !== undefined) return e[field];
    return m[field] ?? "";
  };
  const setVal = (pricing_id, field, value) =>
    setEdits(prev => ({ ...prev, [pricing_id]: { ...prev[pricing_id], [field]: value } }));

  // Cost CAD -> USD at today's rate; markup = sell price - cost(USD).
  const costUsd = (m) => {
    const cc = parseFloat(getVal(m, "cost_cad"));
    if (isNaN(cc) || !rate) return null;
    return cc * rate;
  };
  const markupUsd = (m) => {
    const cu = costUsd(m);
    const sell = parseFloat(getVal(m, "fixed_price"));
    if (cu === null || isNaN(sell)) return null;
    return sell - cu;
  };

  const isDirty = (m) => {
    const e = edits[m.pricing_id];
    if (!e) return false;
    return Object.keys(e).some(k => String(e[k]) !== String(m[k] ?? ""));
  };

  const handleSave = async (m) => {
    const e = edits[m.pricing_id] || {};
    const payload = {};
    ["cost_cad", "fixed_price"].forEach(k => {
      if (e[k] !== undefined) {
        const v = e[k] === "" ? null : parseFloat(e[k]);
        payload[k] = isNaN(v) ? null : v;
      }
    });
    setSaving(prev => ({ ...prev, [m.pricing_id]: true }));
    try {
      await api.patch(`/admin/pricing/${m.pricing_id}`, payload);
      setMeds(prev => prev.map(x => x.pricing_id === m.pricing_id ? { ...x, ...payload } : x));
      setSaved(prev => ({ ...prev, [m.pricing_id]: true }));
      setTimeout(() => setSaved(prev => { const n = { ...prev }; delete n[m.pricing_id]; return n; }), 2200);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[m.pricing_id]; return n; });
    }
  };

  const numInput = (m, field, placeholder = "n/a") => (
    <input
      type="number" step="0.01" min="0"
      value={getVal(m, field)}
      onChange={e => setVal(m.pricing_id, field, e.target.value)}
      placeholder={placeholder}
      className="w-[72px] h-8 px-2 text-right text-sm font-manrope rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 focus:outline-none focus:border-paw-accent text-paw-text dark:text-white placeholder-white/25 transition-colors"
    />
  );

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-[1500px]" data-testid="admin-pricing">
      <div className="mb-6 sm:mb-8">
        <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Pricing Engine</p>
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight text-paw-text dark:text-white">Medication Pricing</h1>
        <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-2 max-w-3xl">
          One row per pack size. Cost is your CAD acquisition price for that pack; Sell is the USD price for the whole pack;
          markup is sell minus cost (USD). Shipping and cost convert at today's
          Bank of Canada rate{rate ? <> (<span className="text-paw-text-secondary dark:text-white/70">1 CAD = ${Number(rate).toFixed(4)} USD</span>)</> : ""}.
        </p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-paw-text-muted dark:text-white/40 pointer-events-none" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter by medication name…"
            className="w-full h-10 pl-9 pr-4 rounded-xl bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent text-paw-text dark:text-white placeholder-white/30"
          />
        </div>
        <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">
          {loading ? "Loading…" : `${filtered.length} of ${meds.length}`}
        </p>
      </div>

      <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl overflow-x-auto">
        <table className="w-full">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
            <tr className="text-left">
              <th className="px-4 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Name</th>
              <th className="px-4 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium hidden xl:table-cell">Strength</th>
              <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Pack</th>
              <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Cost (CAD)</th>
              <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Sell ($)</th>
              <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Markup ($)</th>
              <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Ship (USD)</th>
              <th className="px-4 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-center w-20">Save</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-paw-text-muted dark:text-white/40 font-manrope text-sm">No medications match "{query}"</td></tr>
            ) : filtered.map((m) => {
              const mk = markupUsd(m);
              return (
              <tr key={m.pricing_id || `${m.name}-${m.strength}-${m.pack_size}`} className="border-b border-paw-border dark:border-white/5 last:border-0 hover:bg-white/[0.015] transition-colors">
                <td className="px-4 py-2.5 font-manrope text-sm text-paw-text dark:text-white">
                  {m.name}
                  {m.price_assumed && <span className="ml-2 align-middle text-[10px] uppercase tracking-wide text-amber-300/80 bg-amber-400/10 px-1.5 py-0.5 rounded">est. price</span>}
                </td>
                <td className="px-4 py-2.5 font-manrope text-xs hidden xl:table-cell">
                  {m.units_dual ? <span className="text-paw-accent font-medium" title="Canada and US label this inhaler at different mcg">{m.units_dual}</span> : <span className="text-paw-text-muted dark:text-white/55">{m.strength || "n/a"}</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-manrope text-sm text-paw-text-muted dark:text-white/55">{m.pack_size ? `${m.pack_size}${m.pack_unit ? " " + m.pack_unit : ""}` : "n/a"}</td>
                <td className="px-2 py-1.5 text-right">{numInput(m, "cost_cad")}</td>
                <td className="px-2 py-1.5 text-right">{numInput(m, "fixed_price")}</td>
                <td className={`px-3 py-2.5 text-right font-manrope text-sm font-medium ${mk !== null && mk < 0 ? "text-red-700 dark:text-red-400" : "text-green-700 dark:text-green-400"}`}>{money(mk)}</td>
                <td className="px-3 py-2.5 text-right font-manrope text-sm text-paw-text-muted dark:text-white/55">{money(m.shipping_usd)}</td>
                <td className="px-4 py-2.5 text-center">
                  {saved[m.pricing_id] ? (
                    <span className="font-manrope text-xs text-green-700 dark:text-green-400 font-medium">✓ Saved</span>
                  ) : (
                    <button onClick={() => handleSave(m)} disabled={saving[m.pricing_id] || !isDirty(m)} className="px-3 h-7 rounded-lg bg-paw-accent hover:bg-paw-accent-hover disabled:opacity-60 disabled:cursor-not-allowed font-manrope text-xs text-white font-medium transition-colors">
                      {saving[m.pricing_id] ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Save"}
                    </button>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}
